import React, { Suspense, startTransition, useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { safeLazy } from '../lib/safeLazy';
import { SEO_PAGE_SLUGS } from '../lib/seoPages';
import RagChatWidget from './RagChatWidget';
import PrivacyBanner from './PrivacyBanner';
import '../core-pages.css';

// ── Eager imports: critical pages that users hit first ──
import Landing from '../features/Landing';
import Login from '../features/Login';
import Pricing from '../features/Pricing';
import Discover from '../features/Discover';
import NotFound from '../features/NotFound';
import ProfileView from '../features/ProfileView';
import SeoPage from '../features/SeoPage';
import SupportPage from '../features/SupportPage';

// ── Lazy imports: heavy / behind-auth / rarely visited pages ──
const Dashboard = safeLazy(() => import('../features/Dashboard'));
const Vault = safeLazy(() => import('../features/Vault'));
const EmbedProfile = safeLazy(() => import('../features/EmbedProfile'));
const LedgerPage = safeLazy(() => import('../features/LedgerPage'));
const Blueprint = safeLazy(() => import('../features/Blueprint'));
const DeveloperProgram = safeLazy(() => import('../features/DeveloperProgram'));
const DeveloperDocs = safeLazy(() => import('../features/DeveloperDocs'));
const AdminModeration = safeLazy(() => import('../features/AdminModeration'));
const AdminKnowledge = safeLazy(() => import('../features/AdminKnowledge'));
const AdminSupport = safeLazy(() => import('../features/AdminSupport'));
const Templates = safeLazy(() => import('../features/Templates'));
const TemplateDetail = safeLazy(() => import('../features/TemplateDetail'));
const BuyerPortal = safeLazy(() => import('../features/BuyerPortal'));

const LegalPages = safeLazy(() => import('../features/LegalPage').then((module) => ({
  default: ({ type }) => {
    const Component = module[type];
    return <Component />;
  },
})));

const RouteLoader = () => (
  <div className="linkit-route-loader fixed inset-0 z-50 flex items-center justify-center" role="status" aria-live="polite">
    <div className="linkit-route-loader-stage">
      <div className="linkit-route-loader-brand">
        <span>L</span>
        <strong>linkit</strong>
      </div>
      <p>Preparing the next page</p>
      <i aria-hidden="true"><span /></i>
    </div>
  </div>
);

const Terms = () => <Suspense fallback={<RouteLoader />}><LegalPages type="Terms" /></Suspense>;
const Privacy = () => <Suspense fallback={<RouteLoader />}><LegalPages type="Privacy" /></Suspense>;
const Copyright = () => <Suspense fallback={<RouteLoader />}><LegalPages type="Copyright" /></Suspense>;
const Cookies = () => <Suspense fallback={<RouteLoader />}><LegalPages type="Cookies" /></Suspense>;
const Guidelines = () => <Suspense fallback={<RouteLoader />}><LegalPages type="Guidelines" /></Suspense>;
const Trademarks = () => <Suspense fallback={<RouteLoader />}><LegalPages type="Trademarks" /></Suspense>;

const APP_HOSTS = new Set(['localhost', '127.0.0.1', 'linkit-tan.vercel.app']);

const getCurrentHost = () => {
  if (typeof window === 'undefined') return '';
  return window.location.hostname.toLowerCase();
};

const isCustomProfileHost = (host) => {
  if (!host || APP_HOSTS.has(host) || host.endsWith('.vercel.app')) return false;
  return host.includes('.');
};

const CustomDomainRoot = () => {
  const host = getCurrentHost();
  const [state, setState] = useState({ status: isCustomProfileHost(host) ? 'loading' : 'app', username: '' });

  useEffect(() => {
    if (!isCustomProfileHost(host)) return undefined;
    let cancelled = false;

    fetch(`/api/v1/custom-domain-profile?domain=${encodeURIComponent(host)}`)
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Domain not connected.');
        if (!cancelled) setState({ status: 'profile', username: payload.username || '' });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'missing', username: '' });
      });

    return () => {
      cancelled = true;
    };
  }, [host]);

  if (state.status === 'app') return <Landing />;
  if (state.status === 'profile' && state.username) return <ProfileView injectedUsername={state.username} />;
  if (state.status === 'loading') return <RouteLoader />;

  return (
    <main className="linkit-owned-page linkit-domain-state min-h-screen px-6 py-16 text-slate-950 flex items-center justify-center">
      <section className="w-full max-w-xl text-center">
        <div className="mx-auto mb-8 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950 text-xl font-bold text-white">L</div>
        <p className="text-[11px] font-bold tracking-[0.16em] text-slate-500">Domain connection</p>
        <h1 className="mt-4 text-4xl font-semibold tracking-tight">This domain is not connected yet</h1>
        <p className="mx-auto mt-5 max-w-md text-sm font-medium leading-6 text-slate-600">
          Ask the owner to finish DNS verification from Linkit settings.
        </p>
        <a href="https://linkit-tan.vercel.app" className="mt-8 inline-flex rounded-xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white">
          Visit Linkit
        </a>
      </section>
    </main>
  );
};

const getZone = (path) => (path.startsWith('/dashboard') || path.startsWith('/admin') ? 'studio' : 'public');

const shouldAnimate = (fromPath, toPath) => {
  if (fromPath === toPath) return false;
  return !(getZone(fromPath) === 'studio' && getZone(toPath) === 'studio');
};

const TransitionOverlay = ({ phase }) => (
  <div className={`linkit-transition-overlay linkit-transition-${phase}`} aria-hidden="true">
    <div className="linkit-transition-curtain" />
    <div className="linkit-transition-content">
      <div className="linkit-transition-brand">
        <span>L</span>
        <strong>linkit</strong>
        <i><b /></i>
      </div>
    </div>
  </div>
);

export const AnimatedRoutes = () => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [phase, setPhase] = useState('idle');
  const activeTransition = useRef(false);
  const displayedPath = useRef(location.pathname);
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (location.pathname === displayedPath.current || activeTransition.current) return undefined;

    if (prefersReducedMotion || !shouldAnimate(displayedPath.current, location.pathname)) {
      displayedPath.current = location.pathname;
      startTransition(() => setDisplayLocation(location));
      return undefined;
    }

    activeTransition.current = true;
    const coverTimer = window.setTimeout(() => setPhase('covering'), 0);

    const swapTimer = window.setTimeout(() => {
      displayedPath.current = location.pathname;
      startTransition(() => setDisplayLocation(location));
      setPhase('covered');
    }, 360);

    const revealTimer = window.setTimeout(() => {
      setPhase('revealing');
    }, 520);

    const completeTimer = window.setTimeout(() => {
      setPhase('idle');
      activeTransition.current = false;
    }, 940);

    return () => {
      window.clearTimeout(coverTimer);
      window.clearTimeout(swapTimer);
      window.clearTimeout(revealTimer);
      window.clearTimeout(completeTimer);
      activeTransition.current = false;
    };
  }, [location, prefersReducedMotion]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (displayLocation.hash) {
        const target = document.getElementById(displayLocation.hash.slice(1));
        if (target) {
          target.scrollIntoView({ behavior: 'auto', block: 'start' });
          return;
        }
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
    return () => cancelAnimationFrame(frame);
  }, [displayLocation.hash, displayLocation.pathname]);
  const isMarketingPage = /^\/(pricing|contact|terms|privacy|copyright|cookies|guidelines|trademarks|templates|discover|blueprint|developer-program|developer-docs|$)/.test(displayLocation.pathname) || SEO_PAGE_SLUGS.includes(displayLocation.pathname.slice(1));

  return (
    <>
      <TransitionOverlay phase={phase} />
      <Suspense fallback={<RouteLoader />}>
        <Routes location={displayLocation} key={displayLocation.pathname}>
          <Route path="/admin" element={<AdminModeration />} />
          <Route path="/portal" element={<BuyerPortal />} />
          <Route path="/admin/fortress" element={<AdminModeration />} />
          <Route path="/admin/knowledge" element={<AdminKnowledge />} />
          <Route path="/admin/support" element={<AdminSupport />} />
          <Route path="/vault" element={<Vault />} />
          <Route path="/" element={<CustomDomainRoot />} />
          <Route path="/developer-program" element={<DeveloperProgram />} />
          <Route path="/developer-docs" element={<DeveloperDocs />} />
          <Route path="/login" element={<Login />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/copyright" element={<Copyright />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/guidelines" element={<Guidelines />} />
          <Route path="/trademarks" element={<Trademarks />} />
          <Route path="/dashboard/*" element={<Dashboard />} />
          <Route path="/embed/:username" element={<EmbedProfile />} />
          <Route path="/ledger/:tag" element={<LedgerPage />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/blueprint" element={<Blueprint />} />
          <Route path="/contact" element={<SupportPage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/templates/:templateId" element={<TemplateDetail />} />
          {SEO_PAGE_SLUGS.map((slug) => (
            <Route key={slug} path={`/${slug}`} element={<SeoPage slug={slug} />} />
          ))}
          <Route path="/:username" element={<ProfileView />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {isMarketingPage && <RagChatWidget />}
      {isMarketingPage && <PrivacyBanner />}
    </>
  );
};
